import { useState, useEffect } from 'react'
import { PageHeader, StatusBadge, Modal, EmptyState } from '../components/UI'
import { get, post } from '../utils/api'
import { formatCurrency, formatDate } from '../utils/helpers'
import { IconPlus, IconFileText, IconSend, IconEye, IconLink, IconCopy } from '@tabler/icons-react'

export default function ProposalsPage() {
  const [proposals, setProposals] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [formData, setFormData] = useState({})
  const [saving, setSaving] = useState(false)
  const [selected, setSelected] = useState(null)
  const [shareUrl, setShareUrl] = useState(null)

  const load = async () => { try { setProposals(await get('/proposals')) } catch {} finally { setLoading(false) } }
  useEffect(() => { load() }, [])

  const handleCreate = async () => {
    setSaving(true)
    try { await post('/proposals', formData); setShowAdd(false); setFormData({}); load() }
    catch (err) { alert(err.message) } finally { setSaving(false) }
  }

  const shareProposal = async (id) => {
    try {
      const data = await post(`/public/share-proposal/${id}`)
      setShareUrl(data.share_url)
      navigator.clipboard?.writeText(data.share_url)
      load()
    } catch (err) { alert(err.message) }
  }

  const copyUrl = (url) => { navigator.clipboard?.writeText(url); alert('Link copied!') }

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>

  return (
    <>
      <PageHeader title="Proposals" subtitle={`${proposals.length} proposals`}
        actions={<button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}><IconPlus size={16} className="me-1" />New Proposal</button>} />

      <div className="row row-cards mb-3">
        {[{ l: 'Draft', v: proposals.filter(p => p.status === 'draft').length, c: 'secondary' },
          { l: 'Sent', v: proposals.filter(p => p.status === 'sent').length, c: 'blue' },
          { l: 'Viewed', v: proposals.filter(p => p.status === 'viewed').length, c: 'cyan' },
          { l: 'Accepted', v: proposals.filter(p => p.status === 'accepted').length, c: 'green' }
        ].map(s => (
          <div key={s.l} className="col-6 col-lg-3"><div className="card stat-mini"><div className="card-body">
            <div className="subheader">{s.l}</div><div className="h3 mb-0">{s.v}</div>
          </div></div></div>
        ))}
      </div>

      {proposals.length === 0 ? <EmptyState icon={IconFileText} title="No proposals" message="Create your first proposal." /> : (
        <div className="row row-cards">
          {proposals.map(p => (
            <div key={p.id} className="col-lg-6 col-xl-4">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h4 className="card-title mb-0">{p.title}</h4>
                      <div className="text-secondary" style={{ fontSize: '0.8rem' }}>{p.company} — {p.contact_name}</div>
                    </div>
                    <StatusBadge status={p.status} />
                  </div>
                  <div className="datagrid mb-3">
                    <div className="datagrid-item"><div className="datagrid-title">Subtotal</div><div className="datagrid-content">{formatCurrency(p.subtotal)}</div></div>
                    <div className="datagrid-item"><div className="datagrid-title">Tax ({p.tax_rate}%)</div><div className="datagrid-content">{formatCurrency(p.tax_amount)}</div></div>
                    <div className="datagrid-item"><div className="datagrid-title">Total</div><div className="datagrid-content fw-bold">{formatCurrency(p.total)}</div></div>
                    {p.sent_date && <div className="datagrid-item"><div className="datagrid-title">Sent</div><div className="datagrid-content">{formatDate(p.sent_date, { month: 'short', day: 'numeric' })}</div></div>}
                    {p.expires && <div className="datagrid-item"><div className="datagrid-title">Expires</div><div className="datagrid-content">{formatDate(p.expires, { month: 'short', day: 'numeric' })}</div></div>}
                  </div>
                  {p.content && <div className="text-secondary mb-2" style={{ fontSize: '0.85rem' }}>{p.content}</div>}
                  <div className="d-flex gap-2 flex-wrap">
                    <button className="btn btn-outline-primary btn-sm" onClick={() => setSelected(p)}><IconEye size={14} className="me-1" />View</button>
                    <button className="btn btn-primary btn-sm" onClick={() => shareProposal(p.id)}><IconLink size={14} className="me-1" />{p.share_token ? 'Reshare' : 'Share Link'}</button>
                    {p.share_url && <button className="btn btn-outline-secondary btn-sm" onClick={() => copyUrl(p.share_url)}><IconCopy size={14} className="me-1" />Copy Link</button>}
                  </div>
                  {p.viewed_at && <div className="text-secondary mt-2" style={{ fontSize: '0.75rem' }}>Viewed {formatDate(p.viewed_at.split('T')[0])}{p.accepted_at ? ' · Accepted' : p.declined_at ? ' · Declined' : ''}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <Modal open={!!selected} onClose={() => setSelected(null)} title={selected?.title || 'Proposal'} wide>
        {selected && (
          <div>
            <div className="datagrid mb-3">
              <div className="datagrid-item"><div className="datagrid-title">Company</div><div className="datagrid-content">{selected.company}</div></div>
              <div className="datagrid-item"><div className="datagrid-title">Contact</div><div className="datagrid-content">{selected.contact_name}</div></div>
              <div className="datagrid-item"><div className="datagrid-title">Total</div><div className="datagrid-content fw-bold">{formatCurrency(selected.total)}</div></div>
              <div className="datagrid-item"><div className="datagrid-title">Status</div><div className="datagrid-content"><StatusBadge status={selected.status} /></div></div>
            </div>
            {selected.content && <div className="alert alert-info">{selected.content}</div>}
            {selected.notes && <div className="text-secondary">{selected.notes}</div>}
          </div>
        )}
      </Modal>

      {/* Add Modal */}
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="New Proposal" wide>
        <div className="row g-3">
          {[{ label: 'Title', key: 'title' }, { label: 'Company', key: 'company' }, { label: 'Contact Name', key: 'contact_name' },
            { label: 'Subtotal', key: 'subtotal', type: 'number' }, { label: 'Tax Rate %', key: 'tax_rate', type: 'number' },
            { label: 'Expires', key: 'expires', type: 'date' }].map(f => (
            <div key={f.key} className="col-md-6"><label className="form-label">{f.label}</label>
              <input type={f.type || 'text'} className="form-control" value={formData[f.key] || ''} onChange={e => setFormData({ ...formData, [f.key]: e.target.value })} /></div>))}
          <div className="col-12"><label className="form-label">Content</label><textarea className="form-control" rows={3} value={formData.content || ''} onChange={e => setFormData({ ...formData, content: e.target.value })} /></div>
        </div>
        <div className="d-flex justify-content-end gap-2 mt-4">
          <button className="btn" onClick={() => setShowAdd(false)}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={handleCreate}>{saving ? 'Creating...' : 'Create'}</button>
        </div>
      </Modal>
    </>
  )
}
